import { Check, Copy, ChevronDown } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import MiniChart from "./MiniChart";

function CustomSelect({ value, onChange, options, darkMode, minWidth }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selected = options.find((o) => o.value === value);

  const containerStyle = {
    position: "relative",
    display: "inline-block",
    minWidth: minWidth || 110,
    userSelect: "none",
  };

  const triggerStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    padding: "5px 10px 5px 12px",
    borderRadius: "6px",
    border: darkMode ? "1px solid #444" : "1px solid #d0d0d0",
    backgroundColor: darkMode ? "#252525" : "#fff",
    color: darkMode ? "#e0e0e0" : "#212529",
    cursor: "pointer",
    fontSize: "0.85rem",
    fontWeight: 500,
    transition: "border-color 0.15s, background-color 0.15s",
    outline: "none",
  };

  const menuStyle = {
    position: "absolute",
    top: "calc(100% + 4px)",
    right: 0,
    minWidth: "100%",
    backgroundColor: darkMode ? "#252525" : "#fff",
    border: darkMode ? "1px solid #444" : "1px solid #d0d0d0",
    borderRadius: "6px",
    boxShadow: darkMode
      ? "0 8px 24px rgba(0,0,0,0.5)"
      : "0 8px 24px rgba(0,0,0,0.12)",
    zIndex: 9999,
    overflow: "hidden",
  };

  const optionStyle = (isSelected) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "7px 12px",
    fontSize: "0.85rem",
    cursor: "pointer",
    backgroundColor: isSelected
      ? darkMode ? "#333" : "#f5f5f5"
      : "transparent",
    color: isSelected
      ? darkMode ? "#ff6b6b" : "#ff6b6b"
      : darkMode ? "#e0e0e0" : "#212529",
    fontWeight: isSelected ? 600 : 400,
    transition: "background-color 0.1s",
  });

  return (
    <div ref={ref} style={containerStyle}>
      <div
        style={triggerStyle}
        onClick={() => setOpen((o) => !o)}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = darkMode ? "#666" : "#aaa";
          e.currentTarget.style.backgroundColor = darkMode ? "#2e2e2e" : "#f8f8f8";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = darkMode ? "#444" : "#d0d0d0";
          e.currentTarget.style.backgroundColor = darkMode ? "#252525" : "#fff";
        }}
      >
        <span>{selected?.label ?? value}</span>
        <ChevronDown
          size={13}
          style={{
            flexShrink: 0,
            color: darkMode ? "#888" : "#666",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s",
          }}
        />
      </div>
      {open && (
        <div style={menuStyle}>
          {options.map((opt) => (
            <div
              key={opt.value}
              style={optionStyle(opt.value === value)}
              onClick={() => { onChange(opt.value); setOpen(false); }}
              onMouseEnter={(e) => {
                if (opt.value !== value)
                  e.currentTarget.style.backgroundColor = darkMode ? "#2e2e2e" : "#f5f5f5";
              }}
              onMouseLeave={(e) => {
                if (opt.value !== value)
                  e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              <span>{opt.label}</span>
              {opt.value === value && (
                <Check size={12} style={{ flexShrink: 0, color: "#ff6b6b" }} />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
/**
 * EquipmentDetailsModal Component - Modal displaying equipment details, charts, and API endpoints
 * @param {Object} props
 * @param {Object} props.equipment - Equipment data
 * @param {Object} props.liveData - Live data values for parameters
 * @param {Object} props.timeSeriesData - Time series data for charts
 * @param {Object} props.parameterBounds - Session min/max bounds for each parameter
 * @param {string} props.apiBaseUrl - Base URL for API
 * @param {string} props.displayBaseUrl - Display URL for showing in UI
 * @param {Function} props.onClose - Handler to close the modal
 */
function EquipmentDetailsModal({
  equipment,
  liveData,
  timeSeriesData,
  parameterBounds,
  apiBaseUrl,
  displayBaseUrl,
  onClose,
  darkMode,
}) {
  const [protocolFilter, setProtocolFilter] = useState("rest");
  const [copiedUrl, setCopiedUrl] = useState(null);
  const [durationMinutes, setDurationMinutes] = useState(15);

  const hostItemStyle = {
    rest: {
      backgroundColor: darkMode ? "#0d1f14" : "#f0fdf4",
      border: darkMode ? "1px solid #1a4d2a" : "1px solid #10b981",
    },
    websocket: {
      backgroundColor: darkMode ? "#1f1200" : "#fff7ed",
      border: darkMode ? "1px solid #5a3200" : "1px solid #f97316",
    },
    mqtt: {
      backgroundColor: darkMode ? "#150d22" : "#f8f4ff",
      border: darkMode ? "1px solid #4a2d7a" : "1px solid #9b59b6",
    },
  };

  const DURATION_OPTIONS = [
    { label: "5 min", value: 5 },
    { label: "15 min", value: 15 },
    { label: "30 min", value: 30 },
  ];

  useEffect(() => {
    // Disable body scroll when modal is open
    document.body.style.overflow = "hidden";

    // Re-enable body scroll when modal is closed
    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  const handleCopyUrl = (url) => {
    navigator.clipboard
      .writeText(url)
      .then(() => {
        setCopiedUrl(url);
        setTimeout(() => {
          setCopiedUrl(null);
        }, 2000);
        console.log("URL copied to clipboard:", url);
      })
      .catch((err) => {
        console.error("Failed to copy URL:", err);
      });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="modal-header">
          <h2>
            {equipment.name} ({equipment.instanceId})
          </h2>
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        {/* Modal Body */}
        <div className="modal-body">
          {/* Real-time Visualization Section */}
          <div className="modal-section">
            <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginBottom: "10px", gap: "8px" }}>
              <span style={{ fontSize: "0.85rem", color: "#aaa" }}>Display Range</span>
              <CustomSelect
                value={durationMinutes}
                onChange={(val) => setDurationMinutes(val)}
                options={DURATION_OPTIONS}
                darkMode={darkMode}
              />
            </div>
            <div className="visualization-grid">
              {equipment.parameters.map((param) => {
                const liveValue = liveData[param.tag]?.value;
                const allData = timeSeriesData[param.tag] || [];
                const pointsToShow = (durationMinutes * 60) / 5;
                const chartData = allData.slice(-pointsToShow);
                const bounds = parameterBounds[param.tag];

                return (
                  <div key={param.name} className="parameter-chart">
                    <div className="chart-header">
                      <h4>{param.displayName}</h4>
                      <div className="chart-live-value">
                        <span className="live-indicator">●</span>
                        {liveValue !== undefined
                          ? `${liveValue.toFixed(2)} ${param.unit}`
                          : "N/A"}
                      </div>
                    </div>
                    <div className="chart-container">
                      <MiniChart
                        data={chartData}
                        liveValue={liveValue}
                        unit={param.unit}
                        sessionBounds={bounds}
                        darkMode={darkMode}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* API Endpoints Section - Equipment Specific */}
          <div className="modal-section">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "15px",
              }}
            >
              <h3 className="section-title" style={{ margin: 0 }}>
                Equipment API Endpoints
              </h3>
              <CustomSelect
                value={protocolFilter}
                onChange={(val) => setProtocolFilter(val)}
                options={[
                  { label: "REST", value: "rest" },
                  { label: "WebSocket", value: "websocket" },
                  { label: "MQTT", value: "mqtt" },
                ]}
                darkMode={darkMode}
                minWidth={140}
              />
            </div>
            <div className="endpoints-list">
              {/* REST API Base URL */}
              {protocolFilter === "rest" &&
                (() => {
                  const restBaseUrl = displayBaseUrl;
                  return (
                    <div
                      className="endpoint-item"
                      style={hostItemStyle.rest}
                    >
                      <div
                        className="endpoint-method"
                        style={{ backgroundColor: "#10b981", color: "white" }}
                      >
                        HOST
                      </div>
                      <div
                        className="endpoint-details"
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: "10px",
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <code className="endpoint-path">{restBaseUrl}</code>
                          <p className="endpoint-description">
                            REST API Base URL (all endpoints below are relative
                            to this host)
                          </p>
                        </div>
                        <button
                          onClick={() => handleCopyUrl(restBaseUrl)}
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            padding: "4px",
                            display: "flex",
                            alignItems: "center",
                            color:
                              copiedUrl === restBaseUrl ? "#22c55e" : (darkMode ? "#888" : "#666"),
                            transition: "color 0.2s ease",
                          }}
                          title={
                            copiedUrl === restBaseUrl
                              ? "Copied!"
                              : "Copy Base URL"
                          }
                        >
                          {copiedUrl === restBaseUrl ? (
                            <Check size={16} />
                          ) : (
                            <Copy size={16} />
                          )}
                        </button>
                      </div>
                    </div>
                  );
                })()}

              {/* WebSocket Host URL */}
              {protocolFilter === "websocket" &&
                (() => {
                  const wsBaseUrl = displayBaseUrl
                    .replace("http://", "ws://")
                    .replace("https://", "wss://");
                  return (
                    <div
                      className="endpoint-item"
                      style={hostItemStyle.websocket}
                    >
                      <div
                        className="endpoint-method"
                        style={{ backgroundColor: "#f97316", color: "white" }}
                      >
                        HOST
                      </div>
                      <div
                        className="endpoint-details"
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: "10px",
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <code className="endpoint-path">{wsBaseUrl}</code>
                          <p className="endpoint-description">
                            WebSocket Base URL (all streams below are relative
                            to this host)
                          </p>
                        </div>
                        <button
                          onClick={() => handleCopyUrl(wsBaseUrl)}
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            padding: "4px",
                            display: "flex",
                            alignItems: "center",
                            color: copiedUrl === wsBaseUrl ? "#22c55e" : (darkMode ? "#888" : "#666"),
                            transition: "color 0.2s ease",
                          }}
                          title={
                            copiedUrl === wsBaseUrl
                              ? "Copied!"
                              : "Copy Base URL"
                          }
                        >
                          {copiedUrl === wsBaseUrl ? (
                            <Check size={16} />
                          ) : (
                            <Copy size={16} />
                          )}
                        </button>
                      </div>
                    </div>
                  );
                })()}

              {/* MQTT Broker URL */}
              {protocolFilter === "mqtt" &&
                (() => {
                  const mqttBrokerUrl = "wss://mqtt.hsezy.com";
                  return (
                    <div
                      className="endpoint-item"
                      style={hostItemStyle.mqtt}
                    >
                      <div
                        className="endpoint-method"
                        style={{ backgroundColor: "#9b59b6", color: "white" }}
                      >
                        BROKER
                      </div>
                      <div
                        className="endpoint-details"
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: "10px",
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <code className="endpoint-path">{mqttBrokerUrl}</code>
                          <p className="endpoint-description">
                            MQTT Broker WebSocket URL (connect here to subscribe
                            to topics below)
                          </p>
                        </div>
                        <button
                          onClick={() => handleCopyUrl(mqttBrokerUrl)}
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            padding: "4px",
                            display: "flex",
                            alignItems: "center",
                            color:
                              copiedUrl === mqttBrokerUrl ? "#22c55e" : (darkMode ? "#888" : "#666"),
                            transition: "color 0.2s ease",
                          }}
                          title={
                            copiedUrl === mqttBrokerUrl
                              ? "Copied!"
                              : "Copy Broker URL"
                          }
                        >
                          {copiedUrl === mqttBrokerUrl ? (
                            <Check size={16} />
                          ) : (
                            <Copy size={16} />
                          )}
                        </button>
                      </div>
                    </div>
                  );
                })()}

              {/* Equipment WebSocket - All Parameters */}
              {protocolFilter === "websocket" &&
                (() => {
                  const wsPath = `/ws/stream/${equipment.assetId}/equipment/${equipment.instanceId}`;
                  return (
                    <div className="endpoint-item">
                      <div className="endpoint-method ws">WS</div>
                      <div
                        className="endpoint-details"
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: "10px",
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <code className="endpoint-path">{wsPath}</code>
                          <p className="endpoint-description">
                            WebSocket stream for all parameters of this
                            equipment
                          </p>
                        </div>
                        <button
                          onClick={() => handleCopyUrl(wsPath)}
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            padding: "4px",
                            display: "flex",
                            alignItems: "center",
                            color: copiedUrl === wsPath ? "#22c55e" : (darkMode ? "#888" : "#666"),
                            transition: "color 0.2s ease",
                          }}
                          title={
                            copiedUrl === wsPath ? "Copied!" : "Copy Path"
                          }
                        >
                          {copiedUrl === wsPath ? (
                            <Check size={16} />
                          ) : (
                            <Copy size={16} />
                          )}
                        </button>
                      </div>
                    </div>
                  );
                })()}

              {/* Parameter WebSocket - List each parameter */}
              {protocolFilter === "websocket" &&
                equipment.parameters.map((param) => {
                  const wsParamPath = `/ws/stream/${equipment.assetId}/equipment/${equipment.instanceId}/parameter/${param.name}`;
                  return (
                    <div key={`ws-${param.name}`} className="endpoint-item">
                      <div className="endpoint-method ws">WS</div>
                      <div
                        className="endpoint-details"
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: "10px",
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <code className="endpoint-path">{wsParamPath}</code>
                          <p className="endpoint-description">
                            WebSocket stream for {param.displayName}
                          </p>
                        </div>
                        <button
                          onClick={() => handleCopyUrl(wsParamPath)}
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            padding: "4px",
                            display: "flex",
                            alignItems: "center",
                            color:
                              copiedUrl === wsParamPath ? "#22c55e" : (darkMode ? "#888" : "#666"),
                            transition: "color 0.2s ease",
                          }}
                          title={
                            copiedUrl === wsParamPath
                              ? "Copied!"
                              : "Copy Path"
                          }
                        >
                          {copiedUrl === wsParamPath ? (
                            <Check size={16} />
                          ) : (
                            <Copy size={16} />
                          )}
                        </button>
                      </div>
                    </div>
                  );
                })}

              {/* Timeseries - List each parameter */}
              {protocolFilter === "rest" &&
                equipment.parameters.map((param) => {
                  const restPath = `/assets/${equipment.assetId}/timeseries/${equipment.instanceId}.${param.name}?limit={n}`;
                  return (
                    <div key={`ts-${param.name}`} className="endpoint-item">
                      <div className="endpoint-method get">GET</div>
                      <div
                        className="endpoint-details"
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: "10px",
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <code className="endpoint-path">{restPath}</code>
                          <p className="endpoint-description">
                            Get time series data for {param.displayName}
                          </p>
                        </div>
                        <button
                          onClick={() => handleCopyUrl(restPath)}
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            padding: "4px",
                            display: "flex",
                            alignItems: "center",
                            color: copiedUrl === restPath ? "#22c55e" : (darkMode ? "#888" : "#666"),
                            transition: "color 0.2s ease",
                          }}
                          title={
                            copiedUrl === restPath ? "Copied!" : "Copy Path"
                          }
                        >
                          {copiedUrl === restPath ? (
                            <Check size={16} />
                          ) : (
                            <Copy size={16} />
                          )}
                        </button>
                      </div>
                    </div>
                  );
                })}

              {/* MQTT Topics - List each parameter */}
              {protocolFilter === "mqtt" &&
                equipment.parameters.map((param) => {
                  const mqttTopic = `devices/v1/${equipment.assetId}/${equipment.instanceId}/${param.name}`;
                  return (
                    <div key={`mqtt-${param.name}`} className="endpoint-item">
                      <div
                        className="endpoint-method"
                        style={{ backgroundColor: "#9b59b6", color: "white" }}
                      >
                        MQTT
                      </div>
                      <div
                        className="endpoint-details"
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: "10px",
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <code className="endpoint-path">{mqttTopic}</code>
                          <p className="endpoint-description">
                            MQTT topic for {param.displayName}
                          </p>
                        </div>
                        <button
                          onClick={() => handleCopyUrl(mqttTopic)}
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            padding: "4px",
                            display: "flex",
                            alignItems: "center",
                            color: copiedUrl === mqttTopic ? "#22c55e" : (darkMode ? "#888" : "#666"),
                            transition: "color 0.2s ease",
                          }}
                          title={
                            copiedUrl === mqttTopic ? "Copied!" : "Copy Topic"
                          }
                        >
                          {copiedUrl === mqttTopic ? (
                            <Check size={16} />
                          ) : (
                            <Copy size={16} />
                          )}
                        </button>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="modal-footer">
          <button className="modal-btn secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default EquipmentDetailsModal;
